import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How do I create an account?",
          a: "Click 'Sign Up' on the homepage. You can register with email, phone number, or Google. Complete your profile with photos and information to get started."
        },
        {
          q: "Is KwikLuv free to use?",
          a: "Yes! Women can use all features completely free. Men get 3 free messages, then purchase credits (1 credit = 1 message) to continue chatting."
        },
        {
          q: "Do I need to verify my email?",
          a: "Email verification is optional but recommended. Verified users have better visibility and are more trusted by others."
        }
      ]
    },
    {
      category: "Account & Profile",
      questions: [
        {
          q: "How do I get a verification badge?",
          a: "Go to your profile and click 'Get Verified'. Submit a verification request, and an admin will call you via video call to verify your identity. You'll need to show a simple hand gesture (face optional). The call takes 1-2 minutes and you'll get your badge immediately after approval."
        },
        {
          q: "Can I change my username?",
          a: "Yes! Go to your profile, click 'Edit Profile', and update your name. Changes are saved immediately."
        },
        {
          q: "How do I delete my account?",
          a: "Go to Profile → Settings → Delete Account. Enter your password to confirm. This action is permanent and cannot be undone."
        },
        {
          q: "What's the difference between deactivate and delete?",
          a: "Deactivate hides your profile temporarily (30 days). You can reactivate by logging in. Delete permanently removes all your data."
        }
      ]
    },
    {
      category: "Messaging & Credits",
      questions: [
        {
          q: "How does the credit system work?",
          a: "Men get 3 free messages. After that, each message costs 1 credit. You can buy credits in packages (e.g., 10 credits for R10). Women have unlimited free messages."
        },
        {
          q: "Do credits expire?",
          a: "No, credits never expire. Use them whenever you want."
        },
        {
          q: "Can I get a refund on credits?",
          a: "All sales are final. Credits cannot be refunded once purchased."
        },
        {
          q: "How do I know if someone read my message?",
          a: "Messages show a checkmark when delivered and turn green when read."
        }
      ]
    },
    {
      category: "Creator Subscriptions",
      questions: [
        {
          q: "What are creator subscriptions?",
          a: "Female creators can offer exclusive content to subscribers. You pay a monthly fee (set by the creator) to access their private photos, videos, and text updates."
        },
        {
          q: "How do I subscribe to a creator?",
          a: "Visit a creator's profile and click 'Subscribe'. You'll be charged the monthly fee in credits. Your subscription auto-renews monthly."
        },
        {
          q: "Can I cancel a creator subscription?",
          a: "Yes! Go to 'My Subscriptions' in the sidebar or visit the creator's profile and click 'Cancel Subscription'. You'll keep access until the end of your billing period."
        },
        {
          q: "How do I view my subscriptions?",
          a: "Click 'My Subscriptions' in the sidebar to see all your active subscriptions, renewal dates, and manage them."
        },
        {
          q: "What content do creators post?",
          a: "Creators can post photos, videos, and text status updates. All content is exclusive to subscribers only."
        }
      ]
    },
    {
      category: "Privacy & Safety",
      questions: [
        {
          q: "Who can see my profile?",
          a: "Your profile is visible to other users based on your preferences. You can control who sees your photos and information."
        },
        {
          q: "How do I block someone?",
          a: "Go to their profile and click 'Block User'. They won't be able to see your profile or message you."
        },
        {
          q: "How do I report abuse?",
          a: "Click the ⚠️ Report button on any profile. Select a reason (harassment, fake profile, inappropriate content, spam, scam, or other) and provide details. Our team reviews all reports within 24 hours."
        },
        {
          q: "What happens after I report someone?",
          a: "Our moderation team reviews the report within 24 hours. We may warn, suspend, or ban the reported user depending on the violation. You'll be notified of the outcome."
        },
        {
          q: "Is my payment information secure?",
          a: "Yes! We use PayPal for secure payment processing. We never store your card details."
        },
        {
          q: "Can I see who viewed my profile?",
          a: "Profile views are private. However, you can see who liked you and who you matched with."
        }
      ]
    },
    {
      category: "Features",
      questions: [
        {
          q: "How do video calls work?",
          a: "You can start a video call with someone directly from the chat, just like WhatsApp. The other person can accept or decline. Video calls are free."
        },
        {
          q: "How does the distance filter work?",
          a: "Set your maximum distance (5-500km) in the Discover page filters. You'll only see profiles within that range."
        },
        {
          q: "What are private photos?",
          a: "Female users can upload private photos that require credits to unlock. This gives you control over who sees your intimate photos."
        },
        {
          q: "What does 'Available to travel' mean?",
          a: "This badge shows the user is willing to travel to meet. Look for the ✈️ icon on their profile."
        },
        {
          q: "What does 'Parking available' mean?",
          a: "This badge (🅿️) indicates the user has parking available at their location for convenient meetups."
        },
        {
          q: "How do I change my location?",
          a: "Go to Profile → Edit Profile → Location. Enter your new location and save."
        }
      ]
    },
    {
      category: "Technical Issues",
      questions: [
        {
          q: "I forgot my password. What do I do?",
          a: "Click 'Forgot Password' on the login page. Enter your email and we'll send you a reset link."
        },
        {
          q: "Why can't I upload photos?",
          a: "Photos must be under 10MB and in JPG/PNG format. Make sure you have a stable internet connection."
        },
        {
          q: "The app is slow or not loading. Help!",
          a: "Try clearing your browser cache, refreshing the page, or using a different browser. If issues persist, contact support."
        },
        {
          q: "I'm not receiving emails from KwikLuv.",
          a: "Check your spam folder. Add support@kwikluv.com to your contacts. If still not receiving, contact us."
        }
      ]
    }
  ];

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Home</Link>
        
        <h1>Frequently Asked Questions</h1>
        <p className="subtitle">Find answers to common questions about KwikLuv</p>

        <div className="faq-search">
          <input 
            type="text" 
            placeholder="Search FAQs..." 
            className="search-input"
          />
        </div>

        <div className="faq-container">
          {faqs.map((category, catIndex) => (
            <div key={catIndex} className="faq-category">
              <h2 className="category-title">{category.category}</h2>
              
              {category.questions.map((faq, qIndex) => {
                const index = `${catIndex}-${qIndex}`;
                const isOpen = openIndex === index;
                
                return (
                  <div key={qIndex} className={`faq-item ${isOpen ? 'open' : ''}`}>
                    <button 
                      className="faq-question"
                      onClick={() => toggleFAQ(index)}
                    >
                      <span>{faq.q}</span>
                      <span className="faq-icon">{isOpen ? '−' : '+'}</span>
                    </button>
                    {isOpen && (
                      <div className="faq-answer">
                        <p>{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <section className="still-need-help">
          <h2>Still Need Help?</h2>
          <p>Can't find what you're looking for? We're here to help!</p>
          <div className="help-buttons">
            <Link to="/contact" className="help-btn">
              Contact Support
            </Link>
            <a href="mailto:support@kwikluv.com" className="help-btn secondary">
              Email Us
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FAQ;
